import {
  selectCurrentLanguage,
  selectLanguageCode,
} from '@arc/unified-payments-frontend/i18n';
import {
  AcceptAuthorizationVerbiageRequestV1,
  AuthorizationVerbiageType,
  RetrieveAuthorizationVerbiageRequestV1,
  RetrieveSurchargeAuthorizationVerbiagePayload,
} from '@arc/unified-payments-frontend/shared-payment';
import { createSelector } from '@ngrx/store';
import {
  BankAuthVerbiageStore,
  selectPaymentReferenceNumber,
  SurchargeAuthVerbiageStore,
} from '../btpay';
import { selectTenantName, selectUserEmail } from '../external-selectors';
import {
  selectIsBuyerOriginApp,
  selectIsUnauthenticatedPaymentLinksUser,
  selectNameOnAccount,
  selectOriginatingApplication,
  selectVirtualOtherwisePrimaryTenantId,
} from '../session-store';
import { selectAllowACH, selectIsGracePeriodOn } from '../payment-rules';

export const selectRetrieveBankAuthVerbiageRequestData = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  selectTenantName,
  selectCurrentLanguage,
  selectOriginatingApplication,
  selectNameOnAccount,
  selectIsUnauthenticatedPaymentLinksUser,
  (
    tenantId,
    tenantName,
    currentLanguage,
    originatingApplication,
    nameOnAccount,
    isUnauthenticatedPaymentLinksUser,
  ) => {
    const authVerbiageRequest: RetrieveAuthorizationVerbiageRequestV1 = {
      language: currentLanguage,
      originApplication: originatingApplication,
      variableData: [
        {
          key: 'NameOnAccount',
          value: nameOnAccount,
        },
        {
          key: 'CompanyName',
          value: tenantName,
        },
      ],
      verbiageType: isUnauthenticatedPaymentLinksUser
        ? AuthorizationVerbiageType.PaymentLinksBankAuthorization
        : AuthorizationVerbiageType.BankAuthorization,
    };

    return {
      tenantId: tenantId,
      request: authVerbiageRequest,
    };
  },
);

export const selectSurchargeAuthorizationText = createSelector(
  SurchargeAuthVerbiageStore,
  (state) => {
    return state?.authorizationText;
  },
);

export const selectSurchargeAgreementText = createSelector(
  SurchargeAuthVerbiageStore,
  (state) => {
    return state?.agreementText;
  },
);

export const selectDisplayedSurchargeText = createSelector(
  selectIsBuyerOriginApp,
  SurchargeAuthVerbiageStore,
  (isBuyerOriginApp, response) => {
    if (response == null) {
      return null;
    }

    if (isBuyerOriginApp) {
      return response.authorizationText;
    }

    return `${response.authorizationText}\n${response.agreementText}`;
  },
);

export const selectBankAuthorizationText = createSelector(
  BankAuthVerbiageStore,
  (state) => {
    return state?.authorizationText;
  },
);

export const selectBankAgreementText = createSelector(
  BankAuthVerbiageStore,
  (state) => {
    return state?.agreementText;
  },
);

export const selectDisplayedBankAuthorizationVerbiage = createSelector(
  BankAuthVerbiageStore,
  (response) =>
    response
      ? `${response.authorizationText}\n${response.agreementText}`
      : null,
);

export const selectAcceptAchAuthorizationVerbiageRequestData = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  selectUserEmail,
  selectOriginatingApplication,
  selectBankAuthorizationText,
  selectBankAgreementText,
  selectPaymentReferenceNumber,
  (
    tenantId,
    loginUser,
    originApplication,
    authorizationText,
    authorizationAgreementText,
    referenceId,
  ) => {
    const request: AcceptAuthorizationVerbiageRequestV1 = {
      loginUser,
      originApplication,
      authorizationText,
      authorizationAgreementText,
      referenceId,
    };

    return {
      tenantId,
      request,
    };
  },
);

export const selectAcceptSurchargeAuthorizationVerbiageRequestData =
  createSelector(
    selectVirtualOtherwisePrimaryTenantId,
    selectUserEmail,
    selectOriginatingApplication,
    selectSurchargeAuthorizationText,
    selectSurchargeAgreementText,
    selectPaymentReferenceNumber,
    (
      tenantId,
      loginUser,
      originApplication,
      authorizationText,
      authorizationAgreementText,
      referenceId,
    ) => {
      const request: AcceptAuthorizationVerbiageRequestV1 = {
        loginUser,
        originApplication,
        authorizationText,
        authorizationAgreementText,
        referenceId,
      };

      return {
        tenantId,
        request,
      };
    },
  );

export const selectVerbiageType = createSelector(
  selectIsUnauthenticatedPaymentLinksUser,
  selectIsGracePeriodOn,
  (isUnauthenticatedPaymentLinksUser, isGracePeriodOn) => {
    let verbiageType: AuthorizationVerbiageType;

    if (isUnauthenticatedPaymentLinksUser) {
      verbiageType = isGracePeriodOn
        ? AuthorizationVerbiageType.PaymentLinksGracePeriodSurchargeAuthorization
        : AuthorizationVerbiageType.PaymentLinksSurchargeAuthorization;
    } else {
      verbiageType = isGracePeriodOn
        ? AuthorizationVerbiageType.GracePeriodSurchargeAuthorization
        : AuthorizationVerbiageType.SurchargeAuthorization;
    }

    return verbiageType;
  },
);

export const selectGetSurchargeAuthorizationVerbiageRequest = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  selectTenantName,
  selectOriginatingApplication,
  selectNameOnAccount,
  selectLanguageCode,
  selectAllowACH,
  selectVerbiageType,
  (
    tenantId,
    tenantName,
    originApplication,
    nameOnAccount,
    language,
    allowACH,
    verbiageType,
  ) => {
    const payload: RetrieveSurchargeAuthorizationVerbiagePayload = {
      allowACH,
      tenantId: tenantId,
      request: {
        originApplication,
        variableData: [
          {
            key: 'NameOnAccount',
            value: nameOnAccount,
          },
          {
            key: 'CompanyName',
            value: tenantName,
          },
        ],
        verbiageType,
        language,
      },
    };
    return payload;
  },
);
